/**
 * Admin Clients List Component
 * Display and manage all clients
 * Access: Admin only via /admin/clients
 */

import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    TextField,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TablePagination,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    Visibility as VisibilityIcon,
    EmailOutlined as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import clientsAdminService from '../../services/clientsAdminService';
import { formatDate } from '../../utils/dateFormatter';
import StatusPill from '../shared/StatusPill';
import EmptyState from '../common/EmptyState';

// A free trial from the sign-up form waits for Beetsol's approval, with no dates until then (K11).
const isWaiting = (client) => client.status === 'FREE_TRIAL' && !client.trial_expires_at;

const ClientsAdminList = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [totalClients, setTotalClients] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteClientId, setDeleteClientId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [resendDialogOpen, setResendDialogOpen] = useState(false);
    const [resendClientId, setResendClientId] = useState(null);
    const [resendLoading, setResendLoading] = useState(false);
    const [activationStatus, setActivationStatus] = useState({});
    const [approveClientId, setApproveClientId] = useState(null);
    const [approving, setApproving] = useState(false);
    const [trialDays, setTrialDays] = useState(14);

    // Load clients on mount and when filters change
    useEffect(() => {
        loadClients();
    }, [page, limit, search]);

    const loadClients = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await clientsAdminService.getAllClients({
                page: page + 1,
                limit,
                search
            });
            setClients(response.data.data);
            setTotalClients(response.data.pagination.total);
            if (response.data.trialDays) setTrialDays(response.data.trialDays);
        } catch (err) {
            console.error('Error loading clients:', err);
            setError('Failed to load clients');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAddClient = () => {
        navigate('/workspace/create');
    };

    const handleEditClient = (clientId) => {
        navigate(`/workspace/edit/${clientId}`);
    };

    const handleViewDetails = (clientId) => {
        navigate(`/workspace/${clientId}`);
    };

    const handleDeleteClick = (clientId) => {
        setDeleteClientId(clientId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteClientId) return;

        try {
            setDeleting(true);
            // Deactivate, never delete (S-14). Removed 17 Oct 2026 (S-14): the delete and its messages.
            await clientsAdminService.deleteClient(deleteClientId);
            setSuccess('Workspace deactivated');
            setDeleteDialogOpen(false);
            setDeleteClientId(null);
            loadClients();
        } catch (err) {
            console.error('Error deactivating client:', err);
            setError(err.response?.data?.message || 'Failed to deactivate workspace');
        } finally {
            setDeleting(false);
        }
    };

    const handleApproveConfirm = async () => {
        if (!approveClientId) return;
        try {
            setApproving(true);
            setError('');
            const { data } = await clientsAdminService.approveTrial(approveClientId);
            // An invitation that did not go is shown as a problem, with the server's words (K11).
            if (data && data.emailSent === false) setError(data.message);
            else setSuccess('Trial approved');
            setApproveClientId(null);
            loadClients();
        } catch (err) {
            console.error('Error approving trial:', err);
            setError(err.response?.data?.message || 'Failed to approve trial');
        } finally {
            setApproving(false);
        }
    };

    const handleResendInvite = async (clientId) => {
        try {
            // Get activation status first
            const statusResponse = await clientsAdminService.getActivationStatus(clientId);
            setActivationStatus(prev => ({
                ...prev,
                [clientId]: statusResponse.data.data
            }));
            console.log('Activation status:', statusResponse.data);
            if (statusResponse.data.success === false) {
                setError(statusResponse.data.message || 'Failed to check activation status');
                return;
            }
            setResendClientId(clientId);
            setResendDialogOpen(true);
        } catch (err) {
            console.error('Error checking activation status:', err);
            setError('Failed to check activation status');
        }
    };

    const handleResendConfirm = async () => {
        if (!resendClientId) return;

        try {
            setResendLoading(true);
            setError('');
            
            const response = await clientsAdminService.resendActivationEmail(resendClientId);
            
            setSuccess(response.data.message || 'Activation email sent successfully');
            setResendDialogOpen(false);
            setResendClientId(null);
            
            // Reload to update activation status
            setTimeout(() => loadClients(), 2000);
        } catch (err) {
            console.error('Error resending email:', err);
            setError(
                err.response?.data?.message ||
                'Failed to resend activation email'
            );
        } finally {
            setResendLoading(false);
        }
    };

    // Removed 17 Oct 2026 (K11): getStatusColor, the colours of the old status chip (Free trial, Active, Expired,
    // Cancelled); the type and the Active switch now have a column each.

    if (loading && clients.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <h2>Manage Clients</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddClient}
                    >
                        Add Client
                    </Button>
                </Box>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2, backgroundColor:'#A32D2D' }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Search */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by company name, domain, or email..."
                        value={search}
                        onChange={handleSearchChange}
                        variant="outlined"
                        size="small"
                    />
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Domain</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Slug</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>License</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Active Users</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Trial ends</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                                        <EmptyState framed={false} title="No clients found" message={null} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                clients.map((client) => (
                                    <TableRow key={client.id} hover>
                                        <TableCell>
                                            <strong>{client.company_name}</strong>
                                            <br />
                                            <small style={{ color: '#666' }}>{client.admin_email}</small>
                                        </TableCell>
                                        <TableCell>{client.domain}</TableCell>
                                        <TableCell>
                                            <code style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>
                                                {client.slug}
                                            </code>
                                        </TableCell>
                                        {/* The type, and the Active switch (S-14) as a pill (K11). Removed 17 Oct 2026 (K11): the
                                            status chip, outlined when switched off. */}
                                        <TableCell>{client.status === 'FREE_TRIAL' ? 'Free trial' : 'Production'}</TableCell>
                                        <TableCell>
                                            {isWaiting(client) ? (
                                                <StatusPill status="pending" label="Waiting for approval" />
                                            ) : (
                                                <StatusPill status={client.is_active === 1 ? 'active' : 'inactive'} />
                                            )}
                                        </TableCell>
                                        <TableCell>{client.max_trial_users}</TableCell>
                                        <TableCell>
                                            {client.active_user_count}
                                            {(() => {
                                                const available = client.max_trial_users - client.active_user_count;
                                                return (
                                                    <span style={{ 
                                                        marginLeft: '6px', 
                                                        fontSize: '0.75rem',
                                                        color: available < 0 ? 'red' : 'gray'
                                                    }}>
                                                        ({available >= 0 ? `${available} available` : `${Math.abs(available)} over limit`})
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            {client.status !== 'FREE_TRIAL'
                                                ? '-'
                                                : isWaiting(client)
                                                ? <span style={{ color: '#6F6F6C' }}>Starts when approved</span>
                                                : formatDate(`${String(client.trial_expires_at).slice(0, 10)}T00:00:00`)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            {isWaiting(client) && (
                                                <Button size="small" variant="contained" sx={{ mr: 1 }}
                                                    onClick={() => setApproveClientId(client.id)}>
                                                    Approve
                                                </Button>
                                            )}
                                            {/* No invitation from a switched-off workspace (S-14). */}
                                            {client.is_active === 1 && (
                                            <Tooltip title="Send Activation Email">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleResendInvite(client.id)}
                                                    color="info"
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    <EmailIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            )}
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditClient(client.id)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {/* Deactivate replaces Delete (S-14): shown for an Active workspace, never for
                                                Beetsol's own. */}
                                            {client.is_active === 1 && client.id !== 1 && (
                                            <Tooltip title="Deactivate">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Deactivate"
                                                    onClick={() => handleDeleteClick(client.id)}
                                                >
                                                    <BlockIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            )}

                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalClients}
                    rowsPerPage={limit}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </Paper>

            {/* Deactivate Confirmation Dialog (S-14) */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Deactivate workspace?</DialogTitle>
                <DialogContent>
                    Its users can't sign in until you set it to Active again. Nothing is deleted.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        disabled={deleting}
                    >
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Approve a free trial (K11) */}
            <Dialog open={!!approveClientId} onClose={() => setApproveClientId(null)}>
                <DialogTitle>Approve trial?</DialogTitle>
                <DialogContent>
                    Its {trialDays} days start today, and its admin gets an invitation email.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveClientId(null)} disabled={approving}>
                        Cancel
                    </Button>
                    <Button onClick={handleApproveConfirm} variant="contained" disabled={approving}>
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resend Activation Email Confirmation Dialog */}
            <Dialog open={resendDialogOpen} onClose={() => setResendDialogOpen(false)}>
                <DialogTitle>Send Activation Email</DialogTitle>
                <DialogContent>
                    Are you sure you want to send the activation email ? 
                    A 72-hour invitation link will be sent to the admin email.
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setResendDialogOpen(false)} 
                        disabled={resendLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResendConfirm}
                        variant="contained"
                        color="primary"
                        disabled={resendLoading}
                    >
                        {resendLoading ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ClientsAdminList;
